import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Paper,
} from "@mui/material";
import "./DeviceDiagnostics.css";
import { Fragment } from "react";
import MuiDatePicker from "../../Common/FormInputs/MuiDatePicker";
import AppButton from "../../Common/Button/Button";
import DisplayDeviceDiagnosticsData from "./DisplayDeviceDiagnosticsData";
import {
  deviceDiagnosticsPageSize,
  startDateLimit,
} from "./DeviceDiagnosticsConstants";
import { format } from "date-fns";

const DeviceDiagnostics = ({
  setDialog,
  setDialogContents,
  deviceDetails,
  getDeviceDiagnosticsData,
  diagnosticData,
  downloadDeviceDiagnosticReport,
}) => {
  const [deviceDiagnosticsData, setDeviceDiagnosticsData] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeviceDiagnosticsDataList, setShowDeviceDiagnosticsDataList] =
    useState(false);
  const [isDataDownloading, setIsDataDownloading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const getNextPage = () => {
    const newPg = pageIndex + 1;
    setPageIndex(newPg);
    fetchDeviceDiagnosticsData(newPg);
  };

  const getPrevPage = () => {
    const newPg = pageIndex - 1;
    setPageIndex(newPg);
    fetchDeviceDiagnosticsData(newPg);
  };

  const formateDate = (dateFilter) => {
    if (dateFilter) {
      return dateFilter.getTime();
    }
  };

  const fetchDeviceDiagnosticsData = (
    page = pageIndex,
    fromTs = formateDate(startDateFilter),
    toTs = formateDate(endDateFilter)
  ) => {
    setIsLoading(true);
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId ?? "",
        fromTs,
        toTs,
        index: page,
        size: deviceDiagnosticsPageSize,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
  };

  const reset = () => {
    setStartDateFilter(new Date());
    setEndDateFilter(new Date());
    setPageIndex(0);
    const todaysDateTime = new Date(new Date()?.setHours(0, 0, 0, 0));
    fetchDeviceDiagnosticsData(
      0,
      formateDate(todaysDateTime),
      formateDate(new Date())
    );
  };

  const filter = () => {
    setPageIndex(0);
    fetchDeviceDiagnosticsData(0);
  };

  const handleStartDateChange = (date) => {
    setStartDateFilter(date);
    setEndDateFilter(null);
    setShowDeviceDiagnosticsDataList(false);
  };

  const handleEndDateChange = (date) => {
    setEndDateFilter(date);
    setShowDeviceDiagnosticsDataList(false);
  };

  function handleDownloadDiagnosticsDeviceData() {
    setIsDataDownloading(true);
    downloadDeviceDiagnosticReport(
      {
        fromTs: formateDate(startDateFilter),
        toTs: formateDate(endDateFilter),
        deviceId: deviceDetails.deviceId,
        printableStartDate: `${format(startDateFilter, "dd MMM yyyy")}-${format(endDateFilter, "dd MMM yyyy")}`,
      },
      () => {
        setIsDataDownloading(false);
      }
    );
  }

  const minStartDate = (() => {
    const futureDate = new Date();
    return new Date(
      futureDate?.setDate(futureDate?.getDate() - startDateLimit)
    );
  })();

  useEffect(() => {
    setStartDateFilter(new Date());
    setEndDateFilter(new Date());
    setIsLoading(true);
    const todaysDateTime = new Date(new Date()?.setHours(0, 0, 0, 0));
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId,
        fromTs: formateDate(todaysDateTime),
        toTs: Date?.now(),
        page: 0,
        size: deviceDiagnosticsPageSize,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
    setPageIndex(0);
  }, [deviceDetails?.deviceId, getDeviceDiagnosticsData]);

  useEffect(() => {
    setDeviceDiagnosticsData(diagnosticData);
  }, [diagnosticData]);

  return (
    <Fragment>
      <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "minmax(0, 220px) minmax(0, 220px) auto",
            },
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box component="span" sx={{ fontSize: 14 }}>
              From:
            </Box>
            <MuiDatePicker
              label="Start Date"
              value={startDateFilter}
              onChange={handleStartDateChange}
              minDate={minStartDate}
              maxDate={new Date()}
              fullWidth={false}
              sx={{ minWidth: 0, flex: 1 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box component="span" sx={{ fontSize: 14 }}>
              To:
            </Box>
            <MuiDatePicker
              label="End Date"
              value={endDateFilter}
              onChange={handleEndDateChange}
              minDate={startDateFilter}
              maxDate={new Date()}
              fullWidth={false}
              sx={{ minWidth: 0, flex: 1 }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
            <AppButton
              variant="primary"
              size="medium"
              onClick={filter}
              disabled={startDateFilter === null || endDateFilter === null}
            >
              Filter
            </AppButton>
            <AppButton variant="ghost" size="medium" onClick={reset}>
              Reset
            </AppButton>
            <AppButton
              variant="primary"
              size="medium"
              onClick={handleDownloadDiagnosticsDeviceData}
              disabled={
                isDataDownloading ||
                startDateFilter === null ||
                endDateFilter === null
              }
            >
              {isDataDownloading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "inherit" }} />
                  Download
                </Box>
              ) : (
                "Download"
              )}
            </AppButton>
          </Box>
        </Box>

        {isLoading && (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              height: "20vh",
            }}
          >
            <CircularProgress size={60} sx={{ color: "#AFB42B" }} />
          </Box>
        )}

        {!isLoading && showDeviceDiagnosticsDataList && (
          <Fragment>
            <DisplayDeviceDiagnosticsData
              setDialog={setDialog}
              setDialogContents={setDialogContents}
              deviceCategory={deviceDetails.category}
              deviceDiagnosticsData={deviceDiagnosticsData}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <AppButton
                variant="primary"
                size="medium"
                onClick={() => getPrevPage()}
                disabled={pageIndex === 0}
              >
                Previous
              </AppButton>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ fontSize: 14 }}>
                  Page: {pageIndex + 1}
                </Box>
                <AppButton
                  variant="primary"
                  size="medium"
                  onClick={() => getNextPage()}
                  disabled={
                    deviceDiagnosticsData?.length < deviceDiagnosticsPageSize
                  }
                >
                  Next
                </AppButton>
              </Box>
            </Box>
          </Fragment>
        )}
      </Paper>
    </Fragment>
  );
};

export default DeviceDiagnostics;
